import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
  GridLegacy as Grid,
  Typography,
  InputLabel,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import DialogHeader from 'components/dialogs/DialogHeader';
import Button from 'components/Button';
import { useTranslation } from 'react-i18next';
import { editPatientData } from 'api/analytics';
import forge from 'node-forge';
import { useDispatch } from 'react-redux';
import { showSuccessToast, showErrorToast } from 'features/toastSlice';
import {
  DEFAULT_FHIR_BIRTH_DATE,
  resolveBirthInput,
  switchBirthInputMode,
  getBirthInputErrors,
  buildPatientAgePayload,
} from 'utils/patientBirthInput';
import { getEditPatientDataFields } from 'utils/patientsFormConfig';
import BirthInputFields from './BirthInputFields';
import styles from './styles.module.css';

function normalizeBirthInput(birthInput, allowEstimatedAge) {
  if (!allowEstimatedAge && birthInput.birthInputMode === 'estimatedAge') {
    return {
      birthInputMode: 'dateOfBirth',
      birth_date: '',
      estimated_age: '',
      cached_birth_date: '',
      cached_estimated_age: birthInput.estimated_age || '',
    };
  }

  return {
    ...birthInput,
    cached_birth_date: '',
    cached_estimated_age: '',
  };
}

function EditPatientDataDialog({
  open,
  onClose,
  patientData,
  publicKey,
  fields = getEditPatientDataFields(),
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const allowBirthDate = fields.birth_date.visible;
  const allowEstimatedAge = fields.birth_date.visible && fields.birth_date.estimated_age;
  const birthRequired = fields.birth_date.mandatory;

  const genderOptions = [
    { value: 'M', label: t('physio.physio-edit-patient-dialog.gender-male') },
    { value: 'F', label: t('physio.physio-edit-patient-dialog.gender-female') },
    { value: 'U', label: t('physio.physio-edit-patient-dialog.gender-unspecified') },
  ];

  const [formData, setFormData] = useState({
    acquisition_id: '',
    patient_name: '',
    patient_social_name: '',
    birth_date: '',
    estimated_age: '',
    birthInputMode: 'dateOfBirth',
    cached_birth_date: '',
    cached_estimated_age: '',
    start_time: '',
    gender: '',
    medical_record: '',
    status: '',
  });

  const [errors, setErrors] = useState({
    patient_name: '',
    birth_date: '',
    estimated_age: '',
    gender: '',
    medical_record: '',
  });

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (patientData) {
      const birthInput = normalizeBirthInput(
        resolveBirthInput({
          birthDate: patientData.birth_date,
          age: patientData.age,
        }),
        allowEstimatedAge,
      );

      setFormData({
        acquisition_id: patientData.acquisition_id || '',
        patient_name: patientData.patient_name || '',
        patient_social_name: patientData.patient_social_name || '',
        birth_date: birthInput.birth_date,
        estimated_age: birthInput.estimated_age,
        birthInputMode: birthInput.birthInputMode,
        cached_birth_date: birthInput.cached_birth_date,
        cached_estimated_age: birthInput.cached_estimated_age,
        start_time: patientData.start_time || '',
        gender: patientData.gender || '',
        medical_record: patientData.medical_record || '',
        status: patientData.status || '',
      });

      setErrors({
        patient_name: '',
        birth_date: '',
        estimated_age: '',
        gender: '',
        medical_record: '',
      });
    }
  }, [patientData, allowBirthDate, allowEstimatedAge]);

  const isEstimatedMode = formData.birthInputMode === 'estimatedAge';
  const showBirthFields = allowBirthDate;

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleBirthInputModeChange = (event) => {
    const nextMode = event.target.value;
    setFormData((prevData) => switchBirthInputMode(prevData, nextMode));
    setErrors((prev) => ({ ...prev, birth_date: '', estimated_age: '' }));
  };

  const validateForm = () => {
    const birthErrors = showBirthFields
      ? getBirthInputErrors({
          birthInputMode: formData.birthInputMode,
          birthDate: formData.birth_date,
          estimatedAge: formData.estimated_age,
          required: birthRequired,
        })
      : { birth_date: '', estimated_age: '' };

    const newErrors = {
      patient_name:
        fields.full_name.visible && fields.full_name.mandatory && !formData.patient_name.trim()
          ? 'field-required'
          : '',
      birth_date: birthErrors.birth_date,
      estimated_age: birthErrors.estimated_age,
      gender:
        fields.gender.visible && fields.gender.mandatory && !formData.gender.trim()
          ? 'field-required'
          : '',
      medical_record:
        fields.protocol_id.visible && fields.protocol_id.mandatory && !formData.medical_record.trim()
          ? 'field-required'
          : '',
    };

    setErrors(newErrors);

    return !Object.values(newErrors).some(Boolean);
  };

  const encryptData = (data) => {
    const rsa = forge.pki.publicKeyFromPem(publicKey);
    const utf8Bytes = forge.util.encodeUtf8(data);
    const encrypted = rsa.encrypt(utf8Bytes, 'RAW');
    return forge.util.encode64(encrypted);
  };

  const handleSave = async () => {
    if (!validateForm()) {
      dispatch(showErrorToast(t('physio.physio-edit-patient-dialog.fill-required-fields')));
      return;
    }

    if (formData.status !== 'DO') {
      dispatch(showErrorToast(t('physio.physio-edit-patient-dialog.monitorization-not-finished')));
      return;
    }

    setIsLoading(true);

    const age = buildPatientAgePayload({
      birthInputMode: formData.birthInputMode,
      birthDate: formData.birth_date,
      estimatedAge: formData.estimated_age,
      startTime: formData.start_time,
    });

    const encryptedData = {
      acquisition_id: formData.acquisition_id,
      new_patient_data: {
        patient_name: encryptData(formData.patient_name),
        patient_social_name: encryptData(formData.patient_social_name),
        patient_dob: encryptData(isEstimatedMode ? DEFAULT_FHIR_BIRTH_DATE : formData.birth_date),
        gender: formData.gender,
        medical_record: encryptData(formData.medical_record),
        ...(age != null ? { age } : {}),
      },
    };

    try {
      const response = await editPatientData(encryptedData);
      if (response.data && !response.data.error) {
        dispatch(showSuccessToast(t('physio.physio-edit-patient-dialog.success-message')));
        onClose(true);
      } else {
        dispatch(showErrorToast(t('physio.physio-edit-patient-dialog.error-message')));
      }
    } catch {
      dispatch(showErrorToast(t('physio.physio-edit-patient-dialog.error-message')));
      onClose(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={isLoading ? undefined : onClose}>
      <DialogHeader>{t('physio.physio-edit-patient-dialog.edit-patient-data')}</DialogHeader>
      <DialogHeader variant="small">
        <strong>UMI:</strong> {formData.acquisition_id}
        {formData.status !== 'DO' && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            {t('physio.physio-edit-patient-dialog.monitorization-not-finished')}
          </Typography>
        )}
      </DialogHeader>
      <DialogContent className={styles.DialogContent}>
        <Grid container spacing={2}>
          {fields.full_name.visible && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                margin="normal"
                label={`${t('physio.physio-edit-patient-dialog.patient-name')}`}
                name="patient_name"
                value={formData.patient_name}
                onChange={handleChange}
                error={Boolean(errors.patient_name)}
                helperText={errors.patient_name && t(`physio.physio-edit-patient-dialog.${errors.patient_name}`)}
                required={fields.full_name.mandatory}
                disabled={isLoading}
              />
            </Grid>
          )}

          {fields.social_name.visible && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                margin="normal"
                label={t('physio.physio-edit-patient-dialog.patient-social-name')}
                name="patient_social_name"
                value={formData.patient_social_name}
                onChange={handleChange}
                required={fields.social_name.mandatory}
                disabled={isLoading}
              />
            </Grid>
          )}

          {fields.protocol_id.visible && (
            <Grid item xs={6}>
              <TextField
                fullWidth
                margin="normal"
                label={`${t('physio.physio-edit-patient-dialog.medical-record')}`}
                name="medical_record"
                value={formData.medical_record}
                onChange={handleChange}
                error={Boolean(errors.medical_record)}
                helperText={errors.medical_record && t(`physio.physio-edit-patient-dialog.${errors.medical_record}`)}
                required={fields.protocol_id.mandatory}
                disabled={isLoading}
              />
            </Grid>
          )}

          {showBirthFields && (
            <Grid item xs={6} sx={allowEstimatedAge ? { order: 1 } : undefined}>
              <BirthInputFields
                allowEstimatedAge={allowEstimatedAge}
                allowBirthDate={allowBirthDate}
                required={birthRequired}
                birthInputMode={formData.birthInputMode}
                birthDate={formData.birth_date}
                estimatedAge={formData.estimated_age}
                errors={errors}
                isLoading={isLoading}
                onChange={handleChange}
                onModeChange={handleBirthInputModeChange}
              />
            </Grid>
          )}

          {fields.gender.visible && (
            <Grid item xs={6}>
              <FormControl fullWidth margin="normal" error={Boolean(errors.gender)} required={fields.gender.mandatory}>
                <InputLabel id="gender-label">{`${t('physio.physio-edit-patient-dialog.gender')}`}</InputLabel>
                <Select
                  labelId="gender-label"
                  label={`${t('physio.physio-edit-patient-dialog.gender')}${fields.gender.mandatory ? ' *' : ''}`}
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  required={fields.gender.mandatory}
                  disabled={isLoading}
                >
                  {genderOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.gender && (
                  <Typography variant="caption" color="error">
                    {t(`physio.physio-edit-patient-dialog.${errors.gender}`)}
                  </Typography>
                )}
              </FormControl>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={() => onClose(false)} disabled={isLoading}>
          {t('physio.physio-edit-patient-dialog.button-cancel')}
        </Button>
        <Button sx={{ margin: '16px 0' }} color="gradient" onClick={handleSave} disabled={isLoading}>
          {isLoading
            ? t('physio.physio-edit-patient-dialog.button-saving')
            : t('physio.physio-edit-patient-dialog.button-save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default EditPatientDataDialog;
